import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  SummarySection,
  PaperSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 15 - 블루 보더 프로페셔널 레이아웃 (7.html 기반)
const Template15 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  const SectionTitle = ({ children }) => (
    <h2 className="text-base font-bold tracking-wider mb-4 text-[#0d74a7] border-t-2 border-b-2 border-[#0d74a7] py-2.5">
      {children}
    </h2>
  );

  return (
    <div className={`template-15 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      <div className="text-gray-800">
        {/* Header Section */}
        <div className="px-10 pt-10 pb-5">
          <h1 className={getPlaceholderClass(hasRealData.name, "text-[38px] font-bold mb-1 tracking-wider")}>
            {currentData.name}
          </h1>
          <p className={getPlaceholderClass(hasRealData.title, "text-base text-gray-600 mb-2.5")}>
            {currentData.title}
          </p>
          <div className="text-[13px] text-gray-600">
            <p>
              {currentData.phone && (
                <>
                  <span className={getPlaceholderClass(hasRealData.phone)}>{currentData.phone}</span>
                  {(currentData.email || currentData.socialLinks?.length > 0) && <span className="mx-3">|</span>}
                </>
              )}
              {currentData.email && (
                <>
                  <span className={getPlaceholderClass(hasRealData.email)}>{currentData.email}</span>
                  {currentData.socialLinks?.length > 0 && <span className="mx-3">|</span>}
                </>
              )}
              {currentData.socialLinks && currentData.socialLinks.length > 0 && currentData.socialLinks.map((social, index) => (
                <span key={index}>
                  <a href={social.url} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600">
                    {social.url.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                  {index < currentData.socialLinks.length - 1 && <span className="mx-3">|</span>}
                </span>
              ))}
            </p>
          </div>
        </div>

        {/* Summary Section */}
        {currentData.summary && (
          <div className="px-10 pt-7 pb-5">
            <SectionTitle>{titles.summary.toUpperCase()}</SectionTitle>
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm leading-[1.7]"
            />
          </div>
        )}

        {/* Key Strengths Section */}
        {currentData.keyStrengths && currentData.keyStrengths.length > 0 && (
          <div className="px-10 pt-2 pb-5">
            <SectionTitle>{titles.keyStrengths.toUpperCase()}</SectionTitle>
            <ul className="ml-5 list-disc list-outside">
              {currentData.keyStrengths.map((strength, index) => (
                <li key={index} className={`mb-2.5 text-sm leading-relaxed ${getPlaceholderClass(hasRealData.keyStrengths)}`}>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Experience Section */}
        {currentData.experiences.length > 0 && (
          <div className="px-10 pt-2 pb-5">
            <SectionTitle>{titles.experience.toUpperCase()}</SectionTitle>
            {currentData.experiences.map((exp, index) => (
              <div key={index} className="mb-5">
                <div className="flex justify-between mb-2">
                  <p className={getPlaceholderClass(hasRealData.experiences, "text-[15px] font-bold")}>
                    {exp.position}, {exp.company}
                  </p>
                  <p className={getPlaceholderClass(hasRealData.experiences, "text-[15px] font-bold")}>
                    {exp.startDate} - {exp.endDate}
                  </p>
                </div>
                
                {exp.overallDescription && (
                  <p className={`text-sm leading-relaxed my-2.5 whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                    {exp.overallDescription}
                  </p>
                )}

                {exp.projects && exp.projects.length > 0 && (
                  <div className="space-y-4">
                    {exp.projects.map((project, projIndex) => (
                      <div key={projIndex}>
                        <p className={getPlaceholderClass(hasRealData.experiences, "text-sm font-bold mb-2")}>
                          {project.projectName}
                        </p>
                        {project.projectDescription && (
                          <ul className="ml-5 list-disc list-outside">
                            {project.projectDescription.split('\n').map((line, lineIndex) => (
                              line.trim() && (
                                <li key={lineIndex} className={`mb-1 text-sm leading-relaxed ${getPlaceholderClass(hasRealData.experiences)}`}>
                                  {line.trim()}
                                </li>
                              )
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Education Section */}
        {currentData.education.length > 0 && (
          <div className="px-10 pt-2 pb-5">
            <SectionTitle>{titles.education.toUpperCase()}</SectionTitle>
            {currentData.education.map((edu, index) => (
              <div key={index} className="flex justify-between">
                <div>
                  <p className={getPlaceholderClass(hasRealData.education, "text-[15px] font-bold")}>
                    {edu.degree}, {edu.school}
                  </p>
                  {edu.major && (
                    <p className={getPlaceholderClass(hasRealData.education, "text-sm mt-1")}>
                      {edu.major}
                    </p>
                  )}
                </div>
                <p className={getPlaceholderClass(hasRealData.education, "text-[15px] font-bold")}>
                  {edu.startDate} - {edu.endDate}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Papers Section */}
        {hasRealData.papers && currentData.papers.length > 0 && (
          <div className="px-10 pt-2 pb-5">
            <SectionTitle>{titles.papers.toUpperCase()}</SectionTitle>
            <PaperSection
              papers={currentData.papers}
              hasRealData={hasRealData}
              getPaperTypeText={getPaperTypeText}
              itemClassName="mb-4"
              titleClassName="text-[15px] font-bold"
              authorsClassName="text-sm text-gray-600"
              journalClassName="text-sm"
            />
          </div>
        )}

        {/* Certificates Section */}
        {currentData.certificates.length > 0 && (
          <div className="px-10 pt-2 pb-5">
            <SectionTitle>{titles.certificates.toUpperCase()}</SectionTitle>
            {currentData.certificates.map((cert, index) => (
              <div key={index} className="mb-3">
                <p className={getPlaceholderClass(hasRealData.certificates, "text-sm")}>
                  <span className="font-bold">{cert.name}</span> - {cert.issuer} ({cert.date})
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Skills Section - Three columns */}
        {currentData.skills.length > 0 && (
          <div className="px-10 pt-2 pb-10">
            <SectionTitle>{titles.skills.toUpperCase()}</SectionTitle>
            <div className="grid grid-cols-3 gap-4">
              {currentData.skills.map((skill, index) => (
                <div key={index} className={getPlaceholderClass(hasRealData.skills, "text-sm leading-[1.8]")}>
                  {skill}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Template15;